import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gear, Plus, Trash, PencilSimple, CircleNotch, CaretUpDown, ArrowRight, Archive, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { api, registerError } from "@/components/consumption/registerApi";
import { PeoplePicker } from "@/components/consumption/PeoplePicker";
import { MaterialPicker } from "@/components/consumption/MaterialPicker";
import { ConsumptionAuditDashboard } from "@/components/consumption/ConsumptionAuditDashboard";
import { WeldingRobotPanel } from "@/components/consumption/WeldingRobotPanel";
import { useAuth } from "@/contexts/AuthContext";

// SAP needs a logistics area on every consumption line. The drum stands at
// the line, but the stock it is issued from still sits in a store area, so
// each department names the one area its consumption comes out of.
const AreaSelect = ({ siteId, value, onChange, testId }) => {
  const [areas, setAreas] = useState([]);
  useEffect(() => {
    if (!siteId) { setAreas([]); return; }
    api.warehouses(siteId).then(setAreas).catch(() => setAreas([]));
  }, [siteId]);
  // A saved area must stay visible even while the list is loading, otherwise
  // the field looks empty on an already-mapped department.
  const options = value && !areas.some((a) => a.warehouse_id === value)
    ? [{ warehouse_id: value, warehouse_name: "" }, ...areas]
    : areas;
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={!siteId}>
      <SelectTrigger className="h-9 mt-1" data-testid={testId}>
        <SelectValue placeholder={siteId ? "Select logistics area" : "Pick a site first"} />
      </SelectTrigger>
      <SelectContent>
        {options.map((a) => (
          <SelectItem key={a.warehouse_id} value={a.warehouse_id} data-testid={`${testId}-option-${a.warehouse_id}`}>
            {a.warehouse_id}{a.warehouse_name ? ` · ${a.warehouse_name}` : ""}
            {a.has_stock === false && <span className="text-[#98A2B3]"> · no stock on record</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// A department may only point at a cost centre SAP actually has, so the ID is
// picked from SAP's own cost centre master - choosing one also fills in the
// department name and the warehouse SAP keeps against that cost centre.
const CostCentrePicker = ({ siteId, value, onPick, testId }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) { setRows([]); return; }
    setLoading(true);
    api.costCenters(siteId)
      .then((d) => setRows(d.cost_centers || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [siteId]);

  const selected = rows.find((r) => r.cost_center_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!siteId}
          className="h-9 w-full mt-1 flex items-center justify-between px-3 rounded-md border border-[#D0D5DD] bg-white text-sm text-left disabled:opacity-50"
          data-testid={testId}
        >
          <span className={value ? "text-[#101828]" : "text-[#98A2B3]"}>
            {value ? (
              <>
                <span className="font-mono font-bold">{value}</span>
                {selected?.name ? <span className="text-[#667085]"> · {selected.name}</span> : null}
              </>
            ) : (siteId ? "Pick the SAP cost centre" : "Pick a site first")}
          </span>
          <CaretUpDown size={13} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cost centre or department name..." data-testid={`${testId}-search`} />
          <CommandList>
            {loading && <div className="py-3 text-center text-xs text-[#667085]">Reading SAP&apos;s cost centres...</div>}
            {!loading && <CommandEmpty>No cost centre in SAP matches.</CommandEmpty>}
            <CommandGroup>
              {rows.map((r) => (
                <CommandItem
                  key={r.cost_center_id}
                  value={`${r.cost_center_id} ${r.name}`}
                  onSelect={() => { onPick(r); setOpen(false); }}
                  data-testid={`${testId}-option-${r.cost_center_id}`}
                >
                  <span className="font-mono text-xs font-bold text-[#101828] mr-2">{r.cost_center_id}</span>
                  <span className="text-xs text-[#667085]">{r.name}</span>
                  {r.suggested_area_id && (
                    <span className="ml-auto text-[10px] font-mono text-[#0E7C86]">{r.suggested_area_id}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DepartmentForm = ({ sites, people, onCreated }) => {
  const [siteId, setSiteId] = useState("");
  const [name, setName] = useState("");
  const [costCenter, setCostCenter] = useState("");
  const [area, setArea] = useState("");
  const [members, setMembers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      await onCreated({
        site_id: siteId,
        name,
        cost_center_id: costCenter,
        logistics_area_id: area || null,
        member_emails: members,
        supervisor_emails: supervisors,
        manager_emails: managers,
      });
      setName(""); setCostCenter(""); setArea(""); setMembers([]); setSupervisors([]); setManagers([]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 space-y-3" data-testid="department-form">
      <h2 className="font-heading text-base font-bold text-[#101828]">New department</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold text-[#344054]">Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="h-9 mt-1" data-testid="department-form-site">
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s} value={s} data-testid={`department-form-site-option-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold text-[#344054]">Department name</Label>
          <Input className="h-9 mt-1" placeholder="Plating" value={name} onChange={(e) => setName(e.target.value)} data-testid="department-form-name" />
        </div>
        <div>
          <Label className="text-xs font-bold text-[#344054]">SAP cost centre</Label>
          <CostCentrePicker
            siteId={siteId}
            value={costCenter}
            onPick={(cc) => {
              setCostCenter(cc.cost_center_id);
              setName(cc.name || "");
              if (cc.suggested_area_id) setArea(cc.suggested_area_id);
            }}
            testId="department-form-cost-center"
          />
          <p className="text-[11px] text-[#667085] mt-1">
            Read live from SAP&apos;s cost centre master - this is what identifies the department in accounts, and it
            fills in the name and warehouse SAP keeps against it.
          </p>
          <div className="mt-3">
            <Label className="text-xs font-bold text-[#344054]">Logistics area consumption comes from</Label>
            <AreaSelect siteId={siteId} value={area} onChange={setArea} testId="department-form-area" />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold text-[#344054]">Dept. manager</Label>
            <PeoplePicker
              people={people}
              value={managers}
              onChange={setManagers}
              single
              placeholder="Select the department head"
              testId="department-form-manager"
            />
            <p className="text-[11px] text-[#667085] mt-1">
              Keeps this department&apos;s register himself - records the figures and posts the day to SAP. He sees
              this department only, no other plant.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Operators</Label>
            <PeoplePicker
              people={people}
              value={members}
              onChange={setMembers}
              placeholder="Select operators"
              testId="department-form-members"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Supervisors</Label>
            <PeoplePicker
              people={people}
              value={supervisors}
              onChange={setSupervisors}
              placeholder="Select supervisors"
              testId="department-form-supervisors"
            />
          </div>
        </div>
      </div>
      <Button
        className="bg-[#0E7C86] hover:bg-[#0B6B74]"
        disabled={saving || !siteId || !name.trim() || !costCenter.trim()}
        onClick={create}
        data-testid="department-form-submit"
      >
        {saving ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Create department
      </Button>
    </div>
  );
};

const DepartmentEditor = ({ department, people, onSave, onClose }) => {
  const [name, setName] = useState(department.name);
  const [costCenter, setCostCenter] = useState(department.cost_center_id || "");
  const [area, setArea] = useState(department.logistics_area_id || "");
  const [members, setMembers] = useState(department.member_emails || []);
  const [supervisors, setSupervisors] = useState(department.supervisor_emails || []);
  const [managers, setManagers] = useState(department.manager_emails || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        name,
        cost_center_id: costCenter,
        logistics_area_id: area || "",
        member_emails: members,
        supervisor_emails: supervisors,
        manager_emails: managers,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-[#EAECF0] mt-2 pt-2 space-y-2" data-testid={`department-editor-${department.id}`}>
      <Input className="h-9" value={name} onChange={(e) => setName(e.target.value)} data-testid={`department-edit-name-${department.id}`} />
      <div>
        <Label className="text-[11px] font-bold text-[#344054]">SAP cost centre</Label>
        <CostCentrePicker
          siteId={department.site_id}
          value={costCenter}
          onPick={(cc) => {
            setCostCenter(cc.cost_center_id);
            setName(cc.name || name);
            if (cc.suggested_area_id) setArea(cc.suggested_area_id);
          }}
          testId={`department-edit-cost-center-${department.id}`}
        />
      </div>
      <div>
        <Label className="text-[11px] font-bold text-[#344054]">Logistics area</Label>
        <AreaSelect siteId={department.site_id} value={area} onChange={setArea} testId={`department-edit-area-${department.id}`} />
      </div>
      <div>
        <Label className="text-[11px] font-bold text-[#344054]">Dept. manager</Label>
        <PeoplePicker
          people={people}
          value={managers}
          onChange={setManagers}
          single
          placeholder="Select the department head"
          testId={`department-edit-manager-${department.id}`}
        />
      </div>
      <div>
        <Label className="text-[11px] font-bold text-[#344054]">Operators</Label>
        <PeoplePicker
          people={people}
          value={members}
          onChange={setMembers}
          placeholder="Select operators"
          testId={`department-edit-members-${department.id}`}
        />
      </div>
      <div>
        <Label className="text-[11px] font-bold text-[#344054]">Supervisors</Label>
        <PeoplePicker
          people={people}
          value={supervisors}
          onChange={setSupervisors}
          placeholder="Select supervisors"
          testId={`department-edit-supervisors-${department.id}`}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={saving} onClick={save} data-testid={`department-edit-save-${department.id}`}>
          {saving ? <CircleNotch size={13} className="animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="outline" onClick={onClose} data-testid={`department-edit-cancel-${department.id}`}>Cancel</Button>
      </div>
    </div>
  );
};

export default function ConsumptionRegisterSetupPage() {
  const [masters, setMasters] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deptId, setDeptId] = useState("");
  const [processId, setProcessId] = useState("");
  const [editingDept, setEditingDept] = useState(null);
  const [newProcess, setNewProcess] = useState("");
  const [busyItem, setBusyItem] = useState(false);
  const [working, setWorking] = useState(false);
  const [costCentreInfo, setCostCentreInfo] = useState(null);
  const [invalidDepartments, setInvalidDepartments] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("setup");
  const [people, setPeople] = useState([]);
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role || "user");

  const load = useCallback(async () => {
    try {
      const data = await api.masters();
      setMasters(data);
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.sites().then(setSites).catch(() => setSites([]));
    api.people().then(setPeople).catch(() => setPeople([]));
  }, [load]);

  const departments = useMemo(() => masters?.departments || [], [masters]);
  const processes = useMemo(
    () => (masters?.processes || []).filter((p) => p.department_id === deptId),
    [masters, deptId],
  );
  const items = useMemo(
    () => (masters?.items || []).filter((i) => i.process_id === processId),
    [masters, processId],
  );

  useEffect(() => {
    if (!masters) return;
    if (!departments.some((d) => d.id === deptId)) setDeptId(departments[0]?.id || "");
  }, [masters, departments, deptId]);

  useEffect(() => {
    if (!masters || !deptId) return;
    if (!processes.some((p) => p.id === processId)) setProcessId(processes[0]?.id || "");
  }, [masters, deptId, processes, processId]);

  const run = async (fn, successMessage) => {
    setWorking(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      await load();
      return true;
    } catch (err) {
      toast.error(registerError(err).message);
      return false;
    } finally {
      setWorking(false);
    }
  };

  const createDepartment = (body) => run(() => api.createDepartment(body), `${body.name} created`);

  // Nov 2026, user: "remove map from sap now, but provide a refresh button for
  // the department / cost center - for ex P9-WEL is not showing in the cost
  // center which is created by me now." So the blueprint mapper is gone and
  // this simply re-reads SAP's cost centre master, which is what the
  // department form picks from.
  const refreshCostCentres = async () => {
    setSyncing(true);
    try {
      const result = await api.costCenters(undefined, true);
      setCostCentreInfo({ count: result.count ?? (result.cost_centers || []).length, synced_at: result.synced_at });
      setInvalidDepartments(result.invalid_departments || []);
      toast.success(`${result.count ?? (result.cost_centers || []).length} cost centre(s) read from SAP`);
      await load();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setSyncing(false);
    }
  };

  const saveDepartment = (id, patch) => run(() => api.updateDepartment(id, patch), "Department updated");
  const archiveDepartment = (d) => run(() => api.updateDepartment(d.id, { active: false }), `${d.name} archived`);
  const createProcess = () => run(() => api.createProcess({ department_id: deptId, name: newProcess }), `${newProcess} added`).then((ok) => { if (ok) setNewProcess(""); });
  const archiveProcess = (p) => run(() => api.updateProcess(p.id, { active: false }), `${p.name} archived`);

  const mapItem = async (product) => {
    setBusyItem(true);
    try {
      await run(
        () => api.mapItem({
          process_id: processId,
          product_id: product.product_id,
          description: product.description,
          unit_code: product.uom || undefined,
        }),
        `${product.product_id} mapped`,
      );
    } finally {
      setBusyItem(false);
    }
  };

  const selectedDept = departments.find((d) => d.id === deptId);
  const selectedProcess = processes.find((p) => p.id === processId);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="consumption-register-setup-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Gear size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Consumption Register Setup</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6 w-full max-w-[1400px] mx-auto space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Gear size={20} weight="fill" className="text-[#0E7C86]" />
              <h1 className="font-heading text-lg font-bold text-[#101828]">Consumption Register Setup</h1>
            </div>
            <p className="text-sm text-[#667085] mt-0.5 max-w-3xl">
              Departments hold the chemicals, processes are where they are burned, and the mapped materials are what
              the floor sees. A department's operators only ever see their own figures.
            </p>
          </div>
          <Link to="/consumption-register" className="flex items-center gap-1.5 text-[13px] font-bold text-[#0E7C86] hover:text-[#0B6B74]" data-testid="open-register-link">
            Open the register <ArrowRight size={15} weight="bold" />
          </Link>
        </div>

        <div className="flex gap-1 border-b border-[#E4E7EC]" data-testid="setup-tabs">
          {[["setup", "Setup"], ["welding", "Welding Robots"], ["audit", "Consumption Audit"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`text-sm font-bold px-3.5 py-2 -mb-px border-b-2 transition-colors duration-150 ${
                tab === key ? "border-[#0E7C86] text-[#0E7C86]" : "border-transparent text-[#667085] hover:text-[#344054]"
              }`}
              data-testid={`setup-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "audit" ? <ConsumptionAuditDashboard /> : tab === "welding" ? <WeldingRobotPanel isAdmin={isAdmin} /> : (
          <>

        <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 space-y-2" data-testid="cost-centre-refresh">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-base font-bold text-[#101828]">SAP cost centres</h2>
              <p className="text-[12px] text-[#667085] mt-0.5 max-w-2xl">
                Departments can only point at a cost centre SAP actually has. Created a new one - say
                <span className="font-mono font-bold"> P9-WEL</span> - and it is not in the list yet? Refresh and it
                appears straight away.
              </p>
            </div>
            <Button
              className="bg-[#0E7C86] hover:bg-[#0B6B74]"
              disabled={syncing}
              onClick={refreshCostCentres}
              data-testid="cost-centre-refresh-btn"
            >
              {syncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              Refresh cost centres from SAP
            </Button>
          </div>
          {costCentreInfo && (
            <p className="text-[12px] text-[#027A48] font-semibold" data-testid="cost-centre-refresh-result">
              {costCentreInfo.count} cost centre(s) now available to pick from.
            </p>
          )}
          {!!invalidDepartments.length && (
            <div data-testid="cost-centre-invalid">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#B42318]">
                Pointing at a cost centre SAP does not have ({invalidDepartments.length})
              </p>
              <ul className="mt-1 space-y-0.5">
                {invalidDepartments.map((d) => (
                  <li key={d.id} className="text-[12px] text-[#B42318]" data-testid={`cost-centre-invalid-${d.id}`}>
                    {d.name} · site {d.site_id} · <span className="font-mono">{d.cost_center_id || "none"}</span> -
                    edit it and pick a SAP cost centre, or archive it
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DepartmentForm sites={sites} people={people} onCreated={createDepartment} />

        {loading ? (
          <div className="py-16 text-center" data-testid="setup-loading">
            <CircleNotch size={24} weight="bold" className="animate-spin text-[#0E7C86] mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_280px_1fr] gap-4 items-start">
            <div className="bg-white border border-[#E4E7EC] rounded-xl p-3" data-testid="departments-panel">
              <h2 className="font-heading text-sm font-bold text-[#344054] mb-2">Departments</h2>
              {departments.length === 0 && <p className="text-sm text-[#667085]" data-testid="departments-empty">None yet.</p>}
              <div className="space-y-2">
                {departments.map((d) => (
                  <div
                    key={d.id}
                    className={`rounded-sm border p-2.5 cursor-pointer transition-colors duration-150 ${
                      d.id === deptId ? "border-[#0E7C86] bg-[#F0FDFA]" : "border-[#D0D5DD] bg-white hover:bg-[#F9FAFB]"
                    }`}
                    onClick={() => setDeptId(d.id)}
                    data-testid={`department-row-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#101828] truncate">{d.name}</div>
                        <div className="text-[11px] text-[#667085]">
                          site {d.site_id} · <span className="font-mono">{d.cost_center_id}</span>
                          {d.cost_center_name ? <span> · {d.cost_center_name}</span> : null}
                        </div>
                        <div className="text-[11px] text-[#667085]">
                          area <span className="font-mono">{d.logistics_area_id || "not set"}</span>
                        </div>
                        <div className={`text-[11px] ${(d.member_emails || []).length || (d.supervisor_emails || []).length || (d.manager_emails || []).length ? "text-[#667085]" : "text-[#B54708] font-semibold"}`}>
                          {(d.member_emails || []).length} operator(s) · {(d.supervisor_emails || []).length} supervisor(s)
                        </div>
                        {(d.manager_emails || []).map((email) => (
                          <div key={email} className="text-[11px] text-[#0B6B74] font-semibold truncate" data-testid={`department-manager-${d.id}`}>
                            manager {people.find((p) => p.email === email)?.name || email}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingDept(editingDept === d.id ? null : d.id); }}
                          className="text-[#475467] hover:text-[#0E7C86] p-1"
                          data-testid={`department-edit-${d.id}`}
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); archiveDepartment(d); }}
                          disabled={working}
                          className="text-[#475467] hover:text-[#B42318] p-1 disabled:opacity-40"
                          data-testid={`department-archive-${d.id}`}
                        >
                          <Archive size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    {editingDept === d.id && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <DepartmentEditor
                          department={d}
                          people={people}
                          onSave={(patch) => saveDepartment(d.id, patch)}
                          onClose={() => setEditingDept(null)}
                        />
                      </div>
                    )}
                    <div
                      className="mt-2 pt-2 border-t border-[#EAECF0] flex items-center justify-between gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold text-[#344054]">SAP posting</div>
                        <div className="text-[10px] text-[#667085] leading-tight">
                          {d.posting_enabled
                            ? "Live - the supervisor can post each finished day"
                            : "Off - the floor records, nothing reaches SAP"}
                        </div>
                      </div>
                      <Switch
                        checked={!!d.posting_enabled}
                        disabled={working}
                        onCheckedChange={(v) => run(() => api.updateDepartment(d.id, { posting_enabled: v }),
                          v ? `SAP posting is live for ${d.name}` : `SAP posting switched off for ${d.name}`)}
                        data-testid={`department-posting-${d.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E4E7EC] rounded-xl p-3" data-testid="processes-panel">
              <h2 className="font-heading text-sm font-bold text-[#344054] mb-2">
                Processes{selectedDept ? ` · ${selectedDept.name}` : ""}
              </h2>
              {!selectedDept ? (
                <p className="text-sm text-[#667085]">Pick a department first.</p>
              ) : (
                <>
                  <div className="flex gap-2 mb-2">
                    <Input
                      className="h-9"
                      placeholder="Plating line 1"
                      value={newProcess}
                      onChange={(e) => setNewProcess(e.target.value)}
                      data-testid="process-form-name"
                    />
                    <Button
                      className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74] shrink-0"
                      disabled={working || !newProcess.trim()}
                      onClick={createProcess}
                      data-testid="process-form-submit"
                    >
                      <Plus size={14} weight="bold" />
                    </Button>
                  </div>
                  {processes.length === 0 && <p className="text-sm text-[#667085]" data-testid="processes-empty">None yet.</p>}
                  <div className="space-y-1.5">
                    {processes.map((p) => (
                      <div
                        key={p.id}
                        className={`rounded-sm border px-2.5 py-2 flex items-center justify-between gap-2 cursor-pointer transition-colors duration-150 ${
                          p.id === processId ? "border-[#0E7C86] bg-[#F0FDFA]" : "border-[#D0D5DD] bg-white hover:bg-[#F9FAFB]"
                        }`}
                        onClick={() => setProcessId(p.id)}
                        data-testid={`process-row-${p.id}`}
                      >
                        <span className="text-sm font-bold text-[#101828] truncate">{p.name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); archiveProcess(p); }}
                          disabled={working}
                          className="text-[#475467] hover:text-[#B42318] shrink-0 disabled:opacity-40"
                          data-testid={`process-archive-${p.id}`}
                        >
                          <Archive size={14} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="bg-white border border-[#E4E7EC] rounded-xl p-3" data-testid="items-panel">
              <h2 className="font-heading text-sm font-bold text-[#344054] mb-2">
                Materials{selectedProcess ? ` · ${selectedProcess.name}` : ""}
              </h2>
              {!selectedProcess ? (
                <p className="text-sm text-[#667085]">Pick a process first.</p>
              ) : (
                <>
                  <div className="max-w-md mb-3">
                    <MaterialPicker onPick={mapItem} busy={busyItem} />
                    <p className="text-[11px] text-[#667085] mt-1">
                      The material's SAP unit is used as-is. If the unit isn't known locally it is read live from SAP.
                    </p>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm text-[#667085]" data-testid="items-empty">
                      Nothing mapped yet - the floor sees an empty register until a material is mapped here.
                    </p>
                  ) : (
                    <div className="border border-[#E4E7EC] rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F9FAFB]">
                            <TableHead className="text-xs">Material</TableHead>
                            <TableHead className="text-xs">Description</TableHead>
                            <TableHead className="text-xs">Unit</TableHead>
                            <TableHead className="text-xs w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((i) => (
                            <TableRow key={i.id} data-testid={`item-row-${i.product_id}`}>
                              <TableCell className="font-mono text-sm font-bold text-[#101828]">{i.product_id}</TableCell>
                              <TableCell className="text-xs text-[#667085]">{i.description || "—"}</TableCell>
                              <TableCell className="text-xs font-bold text-[#475467]">{i.unit_code}</TableCell>
                              <TableCell>
                                <button
                                  onClick={() => run(() => api.unmapItem(i.id), `${i.product_id} unmapped`)}
                                  disabled={working}
                                  className="text-[#B42318] disabled:opacity-40"
                                  data-testid={`item-unmap-${i.product_id}`}
                                >
                                  <Trash size={14} weight="bold" />
                                </button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
